/*
  Copyright 2019 Esri
  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at
    http://www.apache.org/licenses/LICENSE-2.0
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
*/
var EsriMapComponent_1;
import { __decorate } from "tslib";
import __NG_CLI_RESOURCE__0 from "./esri-map.component.html?ngResource";
import __NG_CLI_RESOURCE__1 from "./esri-map.component.scss?ngResource";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { MapService } from "../shared/map.service";
import { AppControllerService } from "../../app-controller.service";
import IdentifyParameters from "@arcgis/core/rest/support/IdentifyParameters";
import { RequestStatusService } from "../../shared/services/request-status/request-status.service";
import { MapTools } from "../shared/map.tools";
import { AppConfig } from "../../app.config";
import { AflaModuleStrategyService } from "../../shared/services/afla-module-strategy/afla-module-strategy.service";
import { aflaModuleServiceProvider } from "../../shared/services/afla-module-strategy/afla-module-strategy.service.provider";
import { AutoUnsub } from "../../shared/decorators/autounsub.decorator";
import { take } from "rxjs/operators";
let EsriMapComponent = class EsriMapComponent {
    static { EsriMapComponent_1 = this; }
    mapService;
    mapTools;
    requestStatusService;
    appControllerService;
    aflaModuleStrategyService;
    mapLoadedEvent = new EventEmitter();
    // public moduleID;
    showBusyIndicator = true;
    mapViewEl;
    spinner;
    _view;
    _loaded = false;
    layersLoadedSubscription;
    constructor(mapService, mapTools, requestStatusService, appControllerService, aflaModuleStrategyService) {
        this.mapService = mapService;
        this.mapTools = mapTools;
        this.requestStatusService = requestStatusService;
        this.appControllerService = appControllerService;
        this.aflaModuleStrategyService = aflaModuleStrategyService;
        // this.moduleID = +this.appControllerService.selectedModuleID;
        if (aflaModuleStrategyService.overrideMapDefault !== undefined && aflaModuleStrategyService.overrideMapDefault !== null
            && aflaModuleStrategyService.overrideMapDefault.hasOwnProperty("popup") && aflaModuleStrategyService.overrideMapDefault.popup.hasOwnProperty("bufferDistance")) {
            this.mapService.defaultQueryBuffer = aflaModuleStrategyService.overrideMapDefault.popup.bufferDistance;
        }
        else {
            this.mapService.defaultQueryBuffer = 5;
        }
    }
    _zoom = 10;
    get zoom() {
        return this._zoom;
    }
    set zoom(zoom) {
        this._zoom = zoom;
    }
    _center = [23.273294, -33.946994];
    get center() {
        return this._center;
    }
    set center(center) {
        this._center = center;
    }
    _basemap = AppConfig.settings.defaultBasemap;
    get basemap() {
        return this._basemap;
    }
    set basemap(basemap) {
        this._basemap = basemap;
    }
    get mapLoaded() {
        return this._loaded;
    }
    static getRoutingState() {
        if (history.state.geometries) {
            const routeState = JSON.parse(history.state.geometries);
            let geometries = new Array();
            if (routeState instanceof Array) {
                geometries = routeState;
            }
            else {
                geometries.push(routeState);
            }
            return geometries;
        }
    }
    static checkIfGeometriesToAdd(geometries) {
        return geometries && geometries.length > 0;
    }
    ngOnInit() {
        this.mapService.getServerTokens(+this.appControllerService.selectedModuleID).pipe(take(1)).subscribe(result => {
            this.mapService.externalEsriLoader();
            this.layersLoadedSubscription = this.mapService.$layersLoaded.subscribe(loaded => {
                this.mapLoadedEvent.emit(loaded);
            });
            this.mapService.displayMap(this.mapViewEl, {
                center: this.center,
                zoom: this.zoom,
                baseMap: this.basemap
            }, +this.appControllerService.selectedModuleID).then((mapView) => {
                this.requestStatusService.showBusySpinner("LoadingEsriMap");
                // The map has been initialized
                this._view = mapView;
                this._loaded = mapView.ready;
                this._view.popup.dockEnabled = true;
                this._view.popup.dockOptions = {
                    breakpoint: false,
                    buttonEnabled: false,
                    position: "bottom-left"
                };
                this._view.watch("updating", (updateStatus) => {
                    if (updateStatus) {
                        this.spinner.nativeElement.style.display = "block";
                    }
                    else {
                        this.spinner.nativeElement.style.display = "none";
                    }
                });
                if (+this.appControllerService.selectedModuleID) {
                    this.mapService.fetchModuleData(+this.appControllerService.selectedModuleID);
                }
                else {
                    // on search application, we don't have a moduleId until a specific application is selected
                    this.requestStatusService.removeBusySpinner("LoadingEsriMap");
                }
                this.performRouteStateActions(mapView, EsriMapComponent_1.getRoutingState());
                this._view.when(() => {
                    this._view.on("click", (event) => {
                        // Use hitTest to identify the clicked features
                        this._view.hitTest(event).then((response) => {
                            // Get the first feature from the results
                            // @ts-ignore
                            const feature = response.results[0].graphic;
                            if (feature) {
                                // Access the layer information
                                const layer = feature.layer;
                                let url = layer.url;
                                if (url && layer.visible) {
                                    // @ts-ignore
                                    if (layer.type === "feature") {
                                        // @ts-ignore
                                        url += "/" + layer.layerId;
                                        const featureLayer = layer;
                                        this.mapService.executeQueryTask(event, featureLayer, layer.title, mapView, url);
                                    }
                                    else if (layer.type === "map-image") {
                                        // @ts-ignore
                                        const params = new IdentifyParameters();
                                        params.tolerance = 3;
                                        params.width = this._view.width;
                                        params.height = this._view.height;
                                        params.layerOption = "all";
                                        params.returnGeometry = true;
                                        // @ts-ignore
                                        this.mapService.executeIdentifyTask(event, params, layer.url, mapView);
                                    }
                                }
                            }
                        });
                    });
                }).then();
            });
        });
    }
    ngOnDestroy() {
        if (this._view) {
            this._view.graphics.removeAll();
            // unset map from the view so that it is not destroyed
            this._view.map = null;
            // destroy the map view
            this._view.destroy();
            // this.mapService.destroyMap();
        }
        if (this.layersLoadedSubscription !== undefined && this.layersLoadedSubscription !== null) {
            this.layersLoadedSubscription.unsubscribe();
        }
    }
    toggleBuffer(event) {
        this.mapService.toggleBuffer(event);
    }
    addGeometriesOnMap(geometries, layerReference, clearBeforeAdd, zoomToLayerGraphicExtent, customSymbol) {
        return this.mapService.addGeometriesAsGraphics(geometries, this._view, layerReference, clearBeforeAdd, zoomToLayerGraphicExtent, customSymbol);
    }
    clearGraphics(layerReference) {
        this.mapService.clearGraphics(this._view, layerReference);
    }
    removeGraphicLayer(layerReference, graphicUid) {
        this.mapService.removeGraphicLayer(this._view, layerReference, graphicUid);
    }
    removeGraphicsFromMap(layerReference) {
        this.mapService.removeGraphicsLayer(this._view, layerReference);
    }
    showGeometriesOnMap(mapView, geometries, layerReference) {
        this.mapService.addGeometriesAsGraphics(geometries, mapView, layerReference);
    }
    performRouteStateActions(mapView, routeGeometries) {
        if (EsriMapComponent_1.checkIfGeometriesToAdd(routeGeometries)) {
            this.showGeometriesOnMap(mapView, routeGeometries);
        }
    }
    static ctorParameters = () => [
        { type: MapService },
        { type: MapTools },
        { type: RequestStatusService },
        { type: AppControllerService },
        { type: AflaModuleStrategyService }
    ];
    static propDecorators = {
        mapLoadedEvent: [{ type: Output }],
        mapViewEl: [{ type: ViewChild, args: ["mapViewNode", { static: true },] }],
        spinner: [{ type: ViewChild, args: ["spinner", { static: false },] }],
        zoom: [{ type: Input }],
        center: [{ type: Input }],
        basemap: [{ type: Input }]
    };
};
EsriMapComponent = EsriMapComponent_1 = __decorate([
    Component({
        selector: "app-esri-map",
        providers: [aflaModuleServiceProvider],
        template: __NG_CLI_RESOURCE__0,
        standalone: false,
        changeDetection: ChangeDetectionStrategy.OnPush,
        styles: [__NG_CLI_RESOURCE__1]
    }),
    AutoUnsub()
], EsriMapComponent);
export { EsriMapComponent };
